/**
 * Channel-agnostic, agent-facing reactions. Apple tapbacks pin to the bubble on
 * iMessage; everything else (and all of RCS) goes out as a one-emoji message.
 */
export enum MessageReactionName {
  /** Heart. */
  Love = "love",
  /** Thumbs up. */
  Like = "like",
  /** Thumbs down. */
  Dislike = "dislike",
  /** "Haha". */
  Laugh = "laugh",
  /** Exclamation ("!!"). */
  Emphasize = "emphasize",
  /** Question mark ("?"). */
  Question = "question",
  /** Flexed bicep: encouragement, "you've got this". */
  Flex = "flex",
  /** Fire: hype for a pick or a plan. */
  Fire = "fire",
  /** Party popper: celebrating an order, a milestone, good news. */
  Party = "party",
  /** Clapping hands: applause. */
  Clap = "clap",
  /** Folded hands: gratitude. */
  Pray = "pray",
  /** 100: "nailed it", full agreement. */
  Hundred = "hundred",
  /** Raised hands: shared excitement. */
  Hooray = "hooray",
  /** Heart eyes: loving something they showed or said. */
  HeartEyes = "heart_eyes",
}

export const MESSAGE_REACTION_NAMES = Object.values(MessageReactionName);

/** The reactions Apple ships as tapbacks; only these pin to a bubble on iMessage. */
export const TAPBACK_REACTION_NAMES = [
  MessageReactionName.Love,
  MessageReactionName.Like,
  MessageReactionName.Dislike,
  MessageReactionName.Laugh,
  MessageReactionName.Emphasize,
  MessageReactionName.Question,
] as const;

export type TapbackReactionName = (typeof TAPBACK_REACTION_NAMES)[number];

export function isTapbackReaction(
  reaction: MessageReactionName,
): reaction is TapbackReactionName {
  return (TAPBACK_REACTION_NAMES as readonly MessageReactionName[]).includes(
    reaction,
  );
}

/**
 * Parses a stored reaction string back into a `MessageReactionName`, returning
 * `undefined` for null/unknown values so callers can render nothing safely.
 */
export function parseMessageReactionName(
  value: string | null | undefined,
): MessageReactionName | undefined {
  return MESSAGE_REACTION_NAMES.includes(value as MessageReactionName)
    ? (value as MessageReactionName)
    : undefined;
}

/**
 * Glyph for each reaction: the tapback badge on the reacted-to bubble, and the
 * whole message wherever a tapback cannot land.
 */
export function messageReactionNameToEmoji(
  reaction: MessageReactionName,
): string {
  switch (reaction) {
    case MessageReactionName.Love:
      return "❤️";
    case MessageReactionName.Like:
      return "👍";
    case MessageReactionName.Dislike:
      return "👎";
    case MessageReactionName.Laugh:
      return "😂";
    case MessageReactionName.Emphasize:
      return "‼️";
    case MessageReactionName.Question:
      return "❓";
    case MessageReactionName.Flex:
      return "💪";
    case MessageReactionName.Fire:
      return "🔥";
    case MessageReactionName.Party:
      return "🎉";
    case MessageReactionName.Clap:
      return "👏";
    case MessageReactionName.Pray:
      return "🙏";
    case MessageReactionName.Hundred:
      return "💯";
    case MessageReactionName.Hooray:
      return "🙌";
    case MessageReactionName.HeartEyes:
      return "😍";
  }
}

/** `"name" = glyph` legend for the tool schema, so the enum and its description can't drift. */
export function messageReactionLegend(): string {
  return MESSAGE_REACTION_NAMES.map(
    (reaction) => `"${reaction}" = ${messageReactionNameToEmoji(reaction)}`,
  ).join(", ");
}

/**
 * Apple tapback "add" code for each tapback reaction (mirrors
 * `IMessageReactionType`), kept as raw numbers so redo-model stays dependency-free.
 */
export function messageReactionNameToAppleCode(
  reaction: TapbackReactionName,
): number {
  switch (reaction) {
    case MessageReactionName.Love:
      return 2000;
    case MessageReactionName.Like:
      return 2001;
    case MessageReactionName.Dislike:
      return 2002;
    case MessageReactionName.Laugh:
      return 2003;
    case MessageReactionName.Emphasize:
      return 2004;
    case MessageReactionName.Question:
      return 2005;
  }
}
